import { createStore } from 'vuex'
import { useRoute } from 'vue-router'
import population from './population/index.js'
import variantFamilies from './reports/VariantFamilies.js'
import freezeFrames from './reports/FreezeFrames.js'
import productQuality from './reports/ProductQuality.js'
import operationalVariables from './reports/OperationalVariables.js'
import periodisedVariables from './reports/PeriodisedVariables.js'
import dxcReport from './reports/DxcReport.js'
import streamlineReport from './reports/StreamlineReport.js'
import ecuReport from './reports/EcuReport.js'
import timeSeriesReport from './reports/TimeSeriesReport.js'
import productDataReport from './reports/ProductDataReport.js'
import combineDataSources from './reports/CombineDataSources.js'
import componentPrognostics from './reports/ComponentPrognostics.js'
import rootCauseAnalysis from './reports/RootCauseAnalysis.js'
import testProductDiagnostics from './reports/TestProductDiagnostics'
import tpdAutomaticClassification from './reports/Tpd/AutomaticClassification'
import compatibilityMapFile from './fileCompatibilityMapper'
import { httpClient, httpPostClient } from '@/services/httpClient.js'
import { vuexOidcCreateStoreModule } from 'vuex-oidc'
import { oidcSettings, oidcSettingsTest, oidcSettingsDev } from '../oidc-config.js'

// Getting a value from localStorage
const retrievedValue = localStorage.getItem('selectedSASEnvDesc')
const store  = createStore({
    state: {
      bearerToken: null,
      username: null,
      isFreudUser: false,
      isOptOutProductUser: false,
      isRnDAdvancedAnalysisPlatform: false,
      isYQI_Users: false,
      isODP_limited: false,
      isODP_unlimited: false,
      isRnDemployee: false,
      isRnDconsultant: false,
      isGUIUsersTst: false,
      userRoles: [],
      selectedEnvironment: null,
      selectedSASEnvironment: null,
      availableEnvironments: [],
      environmentsStatus: {
        loading: false,
        error: false
      },
      user: null,
      setQueryName: null,
      setCsvFileName: null,
      setUserCanExportData: false,
      setUserExportData: false,
      setUserExportExcel: false,
      setUserExportMD4: false,
      setUserExportCsv: false,
      inspectExportDataSize: 0,
      setCustomReportLink: '',
      setGUIInfoMessage: '',
      setGUIteamsLink: '',
      setGUIBannerLink: '',
      setGUIBannerTitleLink: '',
      setGUIWarningMessage: '',
      reportTypeOptions: [
        { id: 9, name: 'Combine data sources', store: 'combineDataSources' },
        { id: 12, name: 'Component Prognostics', store: 'componentPrognostics' },
        { id: 6, name: 'Diagnostic codes', store: 'dxcReport' },
        { id: 7, name: 'ECU', store: 'ecuReport' },
        { id: 1, name: 'Freeze frames', store: 'freezeFrames' },
        { id: 3, name: 'Operational variables', store: 'operationalVariables' },
        { id: 5, name: 'Periodised variables', store: 'periodisedVariables' },
        { id: 8, name: 'Product data', store: 'productDataReport' },
        { id: 4, name: 'Product quality', store: 'productQuality' },
        { id: 10, name: 'Root Cause analysis', store: 'rootCauseAnalysis' },
        { id: 11, name: 'Time series', store: 'timeSeriesReport' },
        { id: 13, name: 'DTC Grouping', store: 'streamlineReport' },
      ],
      selectedReportType: null,
      csrf: null,
      sessionStatus: {
        sessionId: null,
        parallelSessionId: null
      },
      isTableFullscreen: false
    },
    mutations: {
      setUsername (state, name) {
        state.username = name
      },
      setUserRoles (state, roles) {
        state.userRoles = roles
      },
      setIsFreudUser (state, value) {
        state.isFreudUser = value
      },
      setIsOptOutProductUser (state, value) {
        state.isOptOutProductUser = value
      },
      setIsRnDAdvancedAnalysisPlatform (state, value) {
        state.isRnDAdvancedAnalysisPlatform = value
      },
      setIsODP_limited (state, value) {
        state.isODP_limited = value
      },
      setIsODP_unlimited (state, value) {
        state.isODP_unlimited = value
      },
      setIsRnDemployee (state, value) {
        state.isRnDemployee = value
      },
      setIsRnDconsultant (state, value) {
        state.isRnDconsultant = value
      },
      setIsYQI_Users (state, value) {
        state.isYQI_Users = value
      },
      setIsAP3610001CLAPAdmins (state, value) {
        state.isAP3610001CLAPAdmins = value
      },
      setIsAP3610001CLAPUsers (state, value) {
        state.isAP3610001CLAPUsers = value
      },
      setIsGUIUsersTst (state, value) {
        state.isGUIUsersTst = value
      },
      setBearerToken (state, token) {
        state.bearerToken = `Bearer ${token}`
      },
      setSelectedEnvironment (state, value) {
        localStorage.setItem('selectedEnvDesc', value?.description)
        state.selectedEnvironment = value
        httpClient.defaults.baseURL = value?.url
        httpPostClient.defaults.baseURL = value?.url
      },
      setSelectedSASEnvironment (state, value) {
        localStorage.setItem('selectedSASEnvDesc', value?.description)
        state.selectedSASEnvironment = value
      },
      setAvailableEnvironments (state, value) {
        state.availableEnvironments = value
      },
      setEnvironmentsStatus (state, value) {
        state.environmentsStatus.loading = value.loading
        state.environmentsStatus.error = value.error
      },
      setSelectedReportType (state, value) {
        state.selectedReportType = value
      },
      setUser (state, value) {
        state.user = value
      },
      setQueryName (state, value) {
        state.setQueryName = value
      },
      setCsvFileName (state, value) {
        state.setCsvFileName = value
      },
      // User flag enabling posibility to export data
      setUserCanExportData (state, value) {
        state.setUserCanExportData = value
      },
      // Gui selection to export data
      setUserExportData (state, value) {
        state.setUserExportData = value
      },
      // Gui selection to export data
      setUserExportMD4 (state, value) {
        state.setUserExportMD4 = value
      },
      // Gui selection to export data
      setUserExportExcel (state, value) {
        state.setUserExportExcel = value
      },
      // Gui selection to export data
      setUserExportCsv (state, value) {
        state.setUserExportCsv = value
      },
      // Data size of export data downloaded
      inspectExportDataSize (state, value) {
        state.inspectExportDataSize = value
      },
      // Gui selection to export data
      setCustomReportLink (state, value) {
        state.setCustomReportLink = value
      },
      setGUIWarningMessage (state, value) {
        state.setGUIWarningMessage = value
      },
      setGUIInfoMessage (state, value) {
        state.setGUIInfoMessage = value
      },
      setGUIteamsLink (state, value) {
        state.setGUIteamsLink = value
      },
      setGUIBannerLink (state, value) {
        state.setGUIBannerLink = value
      },
      setGUIBannerTitleLink (state, value) {
        state.setGUIBannerTitleLink = value
      },
      setCsrf (state, value) {
        state.csrf = value
      },
      setSessionStatus (state, value) {
        state.sessionStatus.sessionId = value.sessionId
        state.sessionStatus.parallelSessionId = value.parallelSessionId
      },
      setTableFullscreen(state, value) {
        state.isTableFullscreen = value
      }
    },
    getters: {
      getUsername: state => {
        return state.username
      },
      getBearerToken: state => {
        return state.bearerToken
      },
      getReportTypeOptions: (state) => {
        // Report id mapping
        // { id: 1, name: 'Freeze frames' },
        // { id: 3, name: 'Operational variables' },
        // { id: 4, name: 'Product quality' },
        // { id: 5, name: 'Periodised variables' },
        // { id: 6, name: 'Diagnostic codes' },
        // { id: 7, name: 'ECU', store: 'ecuReport' },
        // { id: 8, name: 'Product data' },
        // { id: 9, name: 'Combine data sources' },
        // { id: 10, name: 'Root Cause analysis' },
        // { id: 11, name: 'Time series' }
        // { id: 12, name: 'Component Prognostics' }
        // { id: 13, name: 'Streamline method' }                                                 


        var filterReports = state.reportTypeOptions.filter(x => x.id !== 2 && x.id !== 10  && x.id !== 13) // Filtering reports by default

        // Filter all reports except for product quality
        if (state.isYQI_Users && !state.isODP_unlimited && !state.isODP_limited) {
          filterReports = filterReports.filter(x => x.id === 4)
        }


        // Filter if is Test Product Diagnostics
        const route= useRoute()
        if(route?.path?.includes('test-product')){
          filterReports = filterReports.filter(x => x.id === 1)
        }

        // Filter all reports except for ov if product name navistar is selected
        if(state.population.productType?.name?.toLowerCase().includes('navistar')){
          filterReports = filterReports.filter(x => x.id === 3)
         }
  
        return filterReports
      },
      getAllSelections: (state, getters) => {
        return !getters.getSelectedReportTypeOption ? null : {
            selectedReport: getters.getSelectedReportTypeOption?.id,
            populationSelections: getters['population/getAllSelections'],
            reportData: getters[`${getters.getSelectedReportTypeOption?.store}/getAllSelections`]
          }

      },
      getAllSelectionsNew: (state, getters) => {
        return !getters.getSelectedReportTypeOption ? null : {
          selectedReport: getters.getSelectedReportTypeOption?.id,
          populationSelections: getters['population/getAllSelections']
        }
      },
      getSelectedReportTypeOption: (state) => {
        return state.selectedReportType ? state.reportTypeOptions.find(x => x.id === state.selectedReportType) : null
      },
      getTableFullscreen: state => state.isTableFullscreen
    },
    actions: {
      loadStateFromFile ({ getters, commit, dispatch }, file) {
        const compatibleFile = compatibilityMapFile(file)
        dispatch('resetReportCreationState')
        dispatch('population/loadStateFromFile', compatibleFile.populationSelections)
        commit('setSelectedReportType', compatibleFile.selectedReport)
        if (compatibleFile.selectedReport === 9) {
          console.log('compatibleFile store' + getters.getSelectedReportTypeOption.store)
          // Loading state from all combined reports
          'operationalVariables' in compatibleFile ? dispatch('operationalVariables/loadStateFromFile', compatibleFile.operationalVariables) : console.log('unknown key')
          'periodisedVariables' in compatibleFile ? dispatch('periodisedVariables/loadStateFromFile', compatibleFile.periodisedVariables) : console.log('unknown key')
          'productQuality' in compatibleFile ? dispatch('productQuality/loadStateFromFile', compatibleFile.productQuality) : console.log('unknown key')
          'diagnosticCodes' in compatibleFile ? dispatch('dxcReport/loadStateFromFile', compatibleFile.diagnosticCodes) : console.log('unknown key')
          'variantFamilies' in compatibleFile ? dispatch('variantFamilies/loadStateFromFile', compatibleFile.variantFamilies) : console.log('unknown key')
           dispatch('combineDataSources/loadStateFromFile', compatibleFile.reportData)
        } else if (compatibleFile.selectedReport === 10) {
          'productQuality' in compatibleFile ? dispatch('productQuality/loadStateFromFile', compatibleFile.productQuality) : console.log('unknown key')
          dispatch('rootCauseAnalysis/loadStateFromFile', compatibleFile.reportData)
        } else if (compatibleFile.selectedReport === 11) {
          'timeSeries' in compatibleFile ? dispatch('timeSeriesReport/loadStateFromFile', compatibleFile.timeSeries) : console.log('unknown key')
        } else {
          dispatch(`${getters.getSelectedReportTypeOption.store}/loadStateFromFile`, compatibleFile.reportData)
        }
      },
      resetReportCreationState ({ getters, dispatch, commit }) {
        dispatch('population/reset')
        if (getters.getSelectedReportTypeOption) {
          dispatch(`${getters.getSelectedReportTypeOption.store}/reset`)
          commit('setSelectedReportType', null)
        }
      },
      updateTableFullscreen({ commit }, value) {
        commit('setTableFullscreen', value)
      }
    },
    modules: {
      population: population,
      variantFamilies: variantFamilies,
      freezeFrames: freezeFrames,
      productQuality: productQuality,
      operationalVariables: operationalVariables,
      periodisedVariables: periodisedVariables,
      dxcReport: dxcReport,
      streamlineReport:streamlineReport,
      ecuReport: ecuReport,
      productDataReport: productDataReport,
      combineDataSources: combineDataSources,
      componentPrognostics: componentPrognostics,
      rootCauseAnalysis: rootCauseAnalysis,
      timeSeriesReport: timeSeriesReport,
      testProductDiagnostics: testProductDiagnostics,
      tpdAutomaticClassification: tpdAutomaticClassification,
      oidc: vuexOidcCreateStoreModule(oidcSettingsDev, { namespaced: true, isAuthenticatedBy: 'access_token' }, {}),
      sasOidc: vuexOidcCreateStoreModule((retrievedValue === 'SNAPROD' || retrievedValue === null ) ? oidcSettings : oidcSettingsTest , { namespaced: true, isAuthenticatedBy: 'access_token' }, {})
    }
  }) 

  export { store }
